import cx from "classnames";
import { useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import RunCodeChanges from "components/Run/elements/CodeChanges";
import { ArrowRight } from "components/icons/generated";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import TextEllipsis from "ds/components/TextEllipsis";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import { EntityChangeType } from "types/generated";
import ChangeTypeIcon from "components/Run/elements/ChangeTypeIcon";
import CardCollapsibleWrapper from "ds/components/Card/Collapsible/components/Wrapper";
import CardHeader from "ds/components/Card/components/Header";
import CardCollapsibleToggle from "ds/components/Card/Collapsible/components/Toggle";
import CardTitle from "ds/components/Card/components/Title";
import CardCollapsibleContent from "ds/components/Card/Collapsible/components/Content";
import { withTestId } from "utils/withTestId";

import EntityChangeResultBadge from "../EntityChangeResultBadge";
import { RUN_CHANGES_LIST_ITEM_CHECKBOX_TEST_ID } from "../constants";
import { EntityChangeWithId } from "../types";
import { useChangesStore } from "../useChangesStore";
import ReplacementIndicator from "./ReplacementIndicator";
import styles from "./styles.module.css";

type RunChangesListItemProps = {
  item: EntityChangeWithId;
  onCheckItem: (item: EntityChangeWithId, checked: boolean) => unknown;
  checked: boolean;
  selectable: boolean;
};

const RunChangesListItem = ({
  item,
  onCheckItem,
  checked,
  selectable,
}: RunChangesListItemProps) => {
  const collapsed = useChangesStore(
    useShallow(({ getChange }) => {
      return getChange(item.id)?.collapsed ?? false;
    })
  );

  const toggleChange = useChangesStore(
    useShallow(({ toggleChange }) => {
      return toggleChange;
    })
  );

  const handleCheckItem = useCallback(() => {
    onCheckItem(item, !checked);
  }, [onCheckItem, item, checked]);

  const { moved, result } = item.metadata;

  const changeType = item.metadata.type;

  const hasNoChanges = item.attributeChanges.length === 0;

  return (
    <CardCollapsibleWrapper isCollapsed={collapsed} onToggle={() => toggleChange(item.id)}>
      <CardHeader suffix={<EntityChangeResultBadge type={result} />}>
        <Box align="center" gap="medium">
          <CardCollapsibleToggle
            ariaLevel={3}
            ariaLabel={
              moved
                ? `${item.previousAddress}
                  moves to
                  ${item.address}`
                : item.address
            }
          />

          {selectable && !item.address.startsWith("output.") && (
            <Toggle
              variant="checkbox"
              id={`change-${item.id}`}
              onChange={handleCheckItem}
              isSelected={checked}
              {...withTestId(RUN_CHANGES_LIST_ITEM_CHECKBOX_TEST_ID)}
              aria-label={`Select ${item.address} for replanning`}
              value={item.address}
            />
          )}

          <ChangeTypeIcon type={item.metadata.type} />

          <TextEllipsis tooltip={item.address} tooltipWidthMode="maxWidthSm">
            {({ className, ...props }) => (
              <CardTitle tag="span" {...props} className={cx(className, styles.panelTitle)}>
                {moved ? (
                  <>
                    {item.previousAddress}
                    <Icon src={ArrowRight} color="secondary" />
                    {item.address}
                  </>
                ) : (
                  item.address
                )}
              </CardTitle>
            )}
          </TextEllipsis>

          {(changeType === EntityChangeType.ReplaceCreateBeforeDestroy ||
            changeType === EntityChangeType.ReplaceDestroyBeforeCreate ||
            changeType === EntityChangeType.CreateAfterForgotten) && (
            <ReplacementIndicator changeType={changeType} />
          )}
        </Box>
      </CardHeader>
      {!collapsed && (
        <CardCollapsibleContent hasHeader fullbleed>
          <Box fullWidth relative className={styles.diffContent}>
            {hasNoChanges && item.metadata.moved ? (
              <Box fullWidth padding="0 x-large x-large x-large">
                <Typography tag="span">Resource moved without any modifications.</Typography>
              </Box>
            ) : (
              <RunCodeChanges items={item.attributeChanges} />
            )}
          </Box>
        </CardCollapsibleContent>
      )}
    </CardCollapsibleWrapper>
  );
};

export default RunChangesListItem;
